import { Component } from '@theme/component';

/**
 * A custom element that manages the product specialties "show more/less" toggle functionality.
 *
 * @typedef {object} ProductSpecialtiesRefs
 * @property {HTMLElement} grid - The specialties grid container
 * @property {HTMLButtonElement} viewAllButton - The view all/less toggle button
 * @property {HTMLElement} viewAllText - The "show more" text element
 * @property {HTMLElement} viewLessText - The "show less" text element
 *
 * @extends {Component<ProductSpecialtiesRefs>}
 */
class ProductSpecialties extends Component {
  requiredRefs = ['grid', 'viewAllButton'];

  /** @type {HTMLElement[]} */
  #hiddenItems = [];
  #isExpanded = false;

  connectedCallback() {
    super.connectedCallback();

    // Get all hidden items
    this.#hiddenItems = Array.from(
      this.refs.grid.querySelectorAll('.product-specialty-item--hidden')
    );

    // Hide button if there are no hidden items
    if (this.#hiddenItems.length === 0) {
      this.refs.viewAllButton.style.display = 'none';
      return;
    }

    // Initialize hidden items with display: none to prevent gap on initial load
    this.#hiddenItems.forEach((item) => {
      item.style.setProperty('display', 'none', 'important');
    });

    // Set up click handler
    this.refs.viewAllButton.addEventListener('click', this.#handleToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.refs.viewAllButton) {
      this.refs.viewAllButton.removeEventListener('click', this.#handleToggle);
    }
  }

  /**
   * @param {Event} e
   */
  #handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    this.#isExpanded = !this.#isExpanded;

    if (this.#isExpanded) {
      // Expand - show all items
      // First, show items but keep them invisible
      this.#hiddenItems.forEach((item) => {
        item.style.removeProperty('display');
        item.style.opacity = '0';
      });

      // Wait for next frame to trigger animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.setAttribute('data-expanded', 'true');
          this.refs.viewAllButton.setAttribute('aria-expanded', 'true');

          this.#hiddenItems.forEach((item) => {
            item.style.removeProperty('opacity');
          });

          if (this.refs.viewAllText) {
            this.refs.viewAllText.style.setProperty('display', 'none', 'important');
          }
          if (this.refs.viewLessText) {
            this.refs.viewLessText.style.removeProperty('display');
          }
        });
      });
    } else {
      // Collapse - hide items beyond max
      this.removeAttribute('data-expanded');
      this.refs.viewAllButton.setAttribute('aria-expanded', 'false');

      if (this.refs.viewAllText) {
        this.refs.viewAllText.style.removeProperty('display');
      }
      if (this.refs.viewLessText) {
        this.refs.viewLessText.style.setProperty('display', 'none', 'important');
      }

      // Listen for transition end on first hidden item
      const hideAfterTransition = (e) => {
        if (e.propertyName === 'opacity' && !this.#isExpanded) {
          this.#hiddenItems.forEach((item) => {
            item.style.setProperty('display', 'none', 'important');
            item.removeEventListener('transitionend', hideAfterTransition);
          });
        }
      };

      // Add listener to first item to detect when transition completes
      if (this.#hiddenItems.length > 0) {
        this.#hiddenItems[0].addEventListener('transitionend', hideAfterTransition);
      }
    }
  };
}

if (!customElements.get('product-specialties')) {
  customElements.define('product-specialties', ProductSpecialties);
}

